'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertTriangle, Info, XCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idCounter = useRef(0);

  const showToast = (toast: Omit<Toast, 'id'>) => {
    const id = `toast-${++idCounter.current}`;
    const newToast = { ...toast, id };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      removeToast(id);
    }, toast.duration || 2000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  useEffect(() => {
    setGlobalToastFunction(showToast);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

const emptySubscribe = () => () => {};

function ToastContainer() {
  const { toasts, removeToast } = useToast();
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);

  if (!mounted) {
    return null;
  }

  return createPortal(
    <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-2 max-w-sm" style={{zIndex: 99999}}>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={() => removeToast(toast.id)}
        />
      ))}
    </div>,
    document.body
  );
}

function ToastItem({
  toast,
  onRemove,
}: {
  toast: Toast;
  onRemove: () => void;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleRemove = () => {
    setIsVisible(false);
    setTimeout(onRemove, 200); // Wait for animation
  };

  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      iconColor: 'text-emerald-500',
      titleColor: 'text-emerald-700',
      descColor: 'text-emerald-700',
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-500',
      titleColor: 'text-red-700',
      descColor: 'text-red-700',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      iconColor: 'text-amber-500',
      titleColor: 'text-amber-700',
      descColor: 'text-amber-700',
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-700',
      descColor: 'text-blue-700',
    },
  };

  const {
    icon: Icon,
    bgColor,
    borderColor,
    iconColor,
    titleColor,
    descColor,
  } = config[toast.type];

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 p-4 rounded-lg border shadow-lg backdrop-blur-sm transition-all duration-200 ease-out',
        bgColor,
        borderColor,
        isVisible
          ? 'translate-x-0 opacity-100 scale-100'
          : 'translate-x-full opacity-0 scale-95',
      )}
    >
      <Icon className={cn('size-5 flex-shrink-0 mt-0.5', iconColor)} />

      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-bold', titleColor)}>{toast.title}</p>
        {toast.description && (
          <p className={cn('text-sm mt-1', descColor)}>{toast.description}</p>
        )}
      </div>

      <button
        onClick={handleRemove}
        className={cn(
          'flex-shrink-0 rounded-md p-1 transition-colors hover:bg-black/10',
          titleColor,
        )}
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Global toast functions (for convenience)
let globalShowToast: ((toast: Omit<Toast, 'id'>) => void) | null = null;

export const setGlobalToastFunction = (
  showToast: (toast: Omit<Toast, 'id'>) => void,
) => {
  globalShowToast = showToast;
};

export const toast = {
  success: (title: string, description?: string, duration?: number) => {
    if (globalShowToast) {
      globalShowToast({ type: 'success', title, description, duration });
    }
  },
  error: (title: string, description?: string, duration?: number) => {
    if (globalShowToast) {
      globalShowToast({ type: 'error', title, description, duration });
    }
  },
  warning: (title: string, description?: string, duration?: number) => {
    if (globalShowToast) {
      globalShowToast({ type: 'warning', title, description, duration });
    }
  },
  info: (title: string, description?: string, duration?: number) => {
    if (globalShowToast) {
      globalShowToast({ type: 'info', title, description, duration });
    }
  },
};
